/**
 * Admin V3 — Compliance — Pending Verifications Queue (G11.A + G11.C + G11.D)
 *
 * Cross-driver queue of documents in verification_status == "pending".
 *
 * G11.A — Operational requirement: admin must NOT be forced to drill
 *         per-driver to find documents awaiting review. The Compliance
 *         Pilot Page embeds this queue.
 * G11.C — Filter by document_type (7 French VTC types per Plan §2.3.B).
 * G11.D — Inline Verify / Reject actions reuse DocumentVerificationModal.
 * G11.E — Each row exposes a deep-link to /admin-v3/drivers/:driverId
 *         (the per-driver Documents tab).
 *
 * Data source: `pending_verifications` array on
 *   GET /admin/compliance/dashboard (G11 backend extension).
 * Each entry: { id, driver_id, driver_name, driver_email,
 *               document_type, version, uploaded_at, expiry_date }.
 *
 * No new backend round-trips on filter/sort — pure client-side over the
 * already-fetched queue (bounded at 100 by the backend).
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Check, X as XIcon, ExternalLink, Eye } from 'lucide-react';
import { SectionCard, EmptyState } from '../../shared/visualTokens';
import { t } from '../../i18n';
import DocumentVerificationModal from '../drivers/DocumentVerificationModal';
import DocumentViewer from '../drivers/DocumentViewer';

const DOC_TYPES = [
  'permis_de_conduire',
  'carte_grise',
  'attestation_assurance',
  'capacite_professionnelle',
  'kbis',
  'casier_judiciaire',
  'justificatif_domicile',
  'identity_document',
];

/**
 * G11.5 — `search` prop wires the page-level CompliancePilotSearchBar query
 * into the queue. The placeholder there promises "search a driver or a state"
 * — until G11.5 it only filtered the profiles_by_state aggregate. Now any
 * typed query also narrows the visible pending rows by driver_name /
 * driver_email substring (case-insensitive). When `search` is empty, the
 * queue behaves exactly as before. Pure client-side over the already-fetched
 * dashboard payload — no extra API calls.
 */
const PendingVerificationsQueue = ({ pending = [], locale = 'fr', onChange, search = '' }) => {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState('all');
  const [verifyTarget, setVerifyTarget] = useState(null);  // { driverId, docId, action }
  const [viewerTarget, setViewerTarget] = useState(null);  // { driverId, document }

  const filtered = useMemo(() => {
    const list = Array.isArray(pending) ? pending : [];
    const q = (search || '').trim().toLowerCase();
    return list.filter((d) => {
      if (typeFilter !== 'all' && d.document_type !== typeFilter) return false;
      if (!q) return true;
      const haystack = [
        d.driver_name, d.driver_email, d.driver_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [pending, typeFilter, search]);

  const handleVerified = useCallback(() => {
    setVerifyTarget(null);
    if (typeof onChange === 'function') onChange();
  }, [onChange]);

  const handleOpenDriver = useCallback(
    (driverId) => { navigate(`/admin/drivers/${driverId}`); },
    [navigate],
  );

  return (
    <SectionCard
      icon={ClipboardList}
      iconClass="bg-sky-100 text-sky-700"
      title={`${t('compliance.queue.title', locale)} (${filtered.length} ${t('compliance.queue.count', locale)})`}
      action={
        /* G11.4 — Visible label + count so the filter doesn't look "empty
           until interaction" (Owner walkthrough Finding 1). The label is
           now sm/medium and the default option shows the type count so the
           admin knows the filter has options even when "Tous" is selected.
           G11.5 — Forensic verdict UI remediation: select gets explicit
           text-slate-900 to override the global body color inheritance
           (index.css body { color: hsl(var(--foreground)) }) that made
           the selected value invisible on the white panel background. */
        <div className="flex items-center gap-2">
          <label
            htmlFor="compliance-queue-doctype-filter"
            className="text-sm font-medium text-slate-800"
          >
            {t('compliance.queue.filter.doctype', locale)} :
          </label>
          <select
            id="compliance-queue-doctype-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300"
            data-testid="compliance-queue-doctype-filter"
          >
            <option value="all">
              {t('compliance.queue.filter.alltypes', locale)} ({DOC_TYPES.length})
            </option>
            {DOC_TYPES.map((tk) => (
              <option key={tk} value={tk}>
                {t(`compliance.doctype.${tk}`, locale)}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={t('compliance.queue.empty.title', locale)}
          desc={t('compliance.queue.empty.desc', locale)}
        />
      ) : (
        <div className="overflow-x-auto" data-testid="compliance-queue-table">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-600">
                <th className="py-2 pr-4">{t('compliance.queue.table.driver', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.queue.table.email', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.queue.table.type', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.queue.table.version', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.queue.table.uploaded_at', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.queue.table.expiry', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.queue.table.actions', locale)}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-b border-slate-100" data-testid={`queue-row-${d.id}`}>
                  <td className="py-2 pr-4 text-slate-900">{d.driver_name}</td>
                  <td className="py-2 pr-4 text-slate-700 text-xs">{d.driver_email || '—'}</td>
                  <td className="py-2 pr-4 text-slate-700">
                    {t(`compliance.doctype.${d.document_type}`, locale)}
                  </td>
                  <td className="py-2 pr-4 text-slate-700">v{d.version}</td>
                  <td className="py-2 pr-4 text-slate-700">{d.uploaded_at || '—'}</td>
                  <td className="py-2 pr-4 text-slate-700">{d.expiry_date || '—'}</td>
                  <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                    {/* G11.2 — Inline document preview, no per-driver navigation needed */}
                    <button
                      type="button"
                      onClick={() => setViewerTarget({ driverId: d.driver_id, document: d })}
                      className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1"
                      data-testid={`queue-view-${d.id}`}
                    >
                      <Eye className="h-3 w-3" />{t('compliance.queue.action.view', locale)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVerifyTarget({ driverId: d.driver_id, docId: d.id, action: 'verify' })}
                      className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1"
                      data-testid={`queue-verify-${d.id}`}
                    >
                      <Check className="h-3 w-3" />{t('compliance.documents.action.verify', locale)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVerifyTarget({ driverId: d.driver_id, docId: d.id, action: 'reject' })}
                      className="text-rose-700 hover:underline text-xs inline-flex items-center gap-1"
                      data-testid={`queue-reject-${d.id}`}
                    >
                      <XIcon className="h-3 w-3" />{t('compliance.documents.action.reject', locale)}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenDriver(d.driver_id)}
                      className="text-slate-700 hover:underline text-xs inline-flex items-center gap-1"
                      data-testid={`queue-open-driver-${d.id}`}
                    >
                      <ExternalLink className="h-3 w-3" />{t('compliance.queue.action.open', locale)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {verifyTarget && (
        <DocumentVerificationModal
          driverId={verifyTarget.driverId}
          documentId={verifyTarget.docId}
          action={verifyTarget.action}
          onClose={() => setVerifyTarget(null)}
          onSuccess={handleVerified}
          locale={locale}
        />
      )}

      {/* G11.2 — Inline DocumentViewer: open document content without
          leaving the Compliance hub. Reuses the existing GridFS-streamed
          binary endpoint (GET /admin/drivers/{id}/documents/{doc}/content). */}
      {viewerTarget && (
        <DocumentViewer
          driverId={viewerTarget.driverId}
          document={viewerTarget.document}
          onClose={() => setViewerTarget(null)}
          locale={locale}
        />
      )}
    </SectionCard>
  );
};

export default PendingVerificationsQueue;
